import { useState, useEffect, useRef } from 'react';
import { Usd } from '@/utils/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabWithTooltip } from '@/components/ui/tab-with-tooltip';
import { 
  ArrowLeft, Settings, Package, Image, Plus, Trash2, 
  ArrowUpDown, Save, RefreshCw, Sparkles, Clock, LayoutGrid,
  Eye, EyeOff, GripVertical, Upload, Loader2
} from 'lucide-react';
import { getToken, getUser } from '@/utils/auth';
import { useToast } from '@/hooks/use-toast';
import { uploadImageToS3 } from '@/utils/s3Upload';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to get auth header
const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LandingSlotManager = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('ribbon');
  
  // Slot configurations for each section
  const [slotConfigs, setSlotConfigs] = useState({
    ribbon: { section: 'ribbon', mode: 'manual', total_slots: 8, slots: [] },
    featured: { section: 'featured', mode: 'manual', total_slots: 6, slots: [] },
    new_arrivals: { section: 'new_arrivals', mode: 'manual', total_slots: 6, slots: [] }
  });
  
  // Available products for assignment
  const [availableProducts, setAvailableProducts] = useState([]);
  
  // Edit slot dialog
  const [editSlotOpen, setEditSlotOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  
  // Image upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Redirect if not admin
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);
  
  // Fetch slot configurations
  useEffect(() => {
    fetchSlotConfigs();
    fetchAvailableProducts();
  }, []);
  
  const fetchSlotConfigs = async () => {
    try {
      const res = await fetch(`${API}/api/landing-slots`);
      if (res.ok) {
        const data = await res.json();
        setSlotConfigs(data);
      }
    } catch (err) {
      console.error('Failed to fetch slot configs:', err);
      toast({ title: 'Error', description: 'Failed to load slot configurations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableProducts = async () => {
    try {
      const res = await fetch(`${API}/api/marketplace/products?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setAvailableProducts(data.products || []);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };
  
  // Handle image upload for slot using R2
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({ title: 'Error', description: 'Please upload a valid image file (JPEG, PNG, WebP, or GIF)', variant: 'destructive' });
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Error', description: 'Image file must be less than 10MB', variant: 'destructive' });
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      // Upload directly to R2 using presigned URL
      const result = await uploadImageToS3(file, 'landing-slots/', (progress) => {
        setUploadProgress(progress);
      });
      
      // Update the editing slot with the new image URL
      setEditingSlot(prev => ({ ...prev, custom_image: result.url }));
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Upload Failed', description: error.message || 'Failed to upload image', variant: 'destructive' });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const updateSectionConfig = async (section, mode, totalSlots) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/landing-slots/${section}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ mode, total_slots: totalSlots })
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Section configuration updated' });
        fetchSlotConfigs();
      } else {
        const error = await res.json();
        toast({ title: 'Error', description: error.detail || 'Failed to update', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update configuration', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const updateSlot = async (section, slotData) => {
    setSaving(true);
    try {
      // Convert custom_price to number or null if empty
      const processedData = {
        ...slotData,
        custom_price: slotData.custom_price !== '' && slotData.custom_price !== null && slotData.custom_price !== undefined
          ? parseFloat(slotData.custom_price)
          : null
      };
      
      const res = await fetch(`${API}/api/landing-slots/${section}/slot`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(processedData)
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Slot updated successfully' });
        setEditSlotOpen(false);
        fetchSlotConfigs();
        // Navigate back to admin dashboard after short delay to show success message
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1000);
      } else {
        const error = await res.json();
        toast({ title: 'Error', description: error.detail || 'Failed to update slot', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update slot', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const clearSlot = async (section, slotNumber) => {
    if (!confirm('Are you sure you want to clear this slot?')) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/landing-slots/${section}/slot/${slotNumber}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Slot cleared' });
        fetchSlotConfigs();
      } else {
        toast({ title: 'Error', description: 'Failed to clear slot', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to clear slot', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const swapSlots = async (section, slotA, slotB) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/landing-slots/${section}/swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ slot_a: slotA, slot_b: slotB })
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Slots swapped' });
        fetchSlotConfigs();
      } else {
        toast({ title: 'Error', description: 'Failed to swap slots', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to swap slots', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const seedNumecis = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/landing-slots/seed-numecis`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Numecis V1 added to ribbon and featured sections' });
        fetchSlotConfigs();
      } else {
        toast({ title: 'Error', description: 'Failed to seed Numecis', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to seed Numecis', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const openEditSlot = (section, slotNumber, existingSlot = null) => {
    setEditingSection(section);
    setEditingSlot({
      slot_number: slotNumber,
      product_id: existingSlot?.product_id || '',
      custom_title: existingSlot?.custom_title || '',
      custom_subtitle: existingSlot?.custom_subtitle || '',
      custom_image: existingSlot?.custom_image || '',
      custom_price: existingSlot?.custom_price || '',
      custom_link: existingSlot?.custom_link || '/marketplace',
      is_active: existingSlot?.is_active !== false
    });
    setEditSlotOpen(true);
  };
  
  const handleProductSelect = (productId) => {
    if (productId === 'custom') {
      setEditingSlot(prev => ({ ...prev, product_id: '' }));
    } else {
      const product = availableProducts.find(p => p.id === productId);
      if (product) {
        setEditingSlot(prev => ({
          ...prev,
          product_id: productId,
          custom_title: product.name,
          custom_subtitle: product.description?.substring(0, 100) || '',
          custom_image: product.images?.[0] || '',
          custom_price: product.price || ''
        }));
      }
    }
  };
  
  const renderSlotCard = (section, slotNumber, slot) => {
    const isEmpty = !slot;
    const config = slotConfigs[section];
    
    return (
      <Card 
        key={slotNumber}
        className={`relative overflow-hidden transition-all duration-200 ${isEmpty ? 'border-dashed border-2' : ''}`}
        style={{ 
          backgroundColor: isEmpty ? '#F5F5F5' : '#FFFFFF',
          borderColor: isEmpty ? '#E3E1DB' : '#E3E1DB'
        }}
      >
        {/* Slot Number Badge */}
        <div 
          className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white z-10"
          style={{ backgroundColor: '#163329' }}
        >
          {slotNumber}
        </div>
        
        {/* Active/Inactive Badge */}
        {slot && (
          <div 
            className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium z-10 ${slot.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
          >
            {slot.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </div>
        )}
        
        {/* Image Area */}
        <div className="w-full h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
          {slot?.custom_image ? (
            <img 
              src={slot.custom_image.startsWith('/api') ? `${API}${slot.custom_image}` : slot.custom_image}
              alt={slot.custom_title || 'Slot image'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-gray-400">
              <Image className="h-8 w-8 mx-auto mb-1" />
              <span className="text-xs">Empty Slot</span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <CardContent className="p-3">
          {slot ? (
            <>
              <h4 className="font-semibold text-sm line-clamp-1" style={{ color: '#163329' }}>
                {slot.custom_title || 'Untitled'}
              </h4>
              <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                {slot.custom_subtitle || 'No description'}
              </p>
              {slot.custom_price && (
                <p className="text-sm font-bold mt-1" style={{ color: '#D86B47' }}>
                  <Usd value={Number(slot.custom_price)} />
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-400 text-center">Click to add content</p>
          )}
          
          {/* Actions */}
          <div className="flex gap-1 mt-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-7 text-xs"
              onClick={() => openEditSlot(section, slotNumber, slot)}
              disabled={config.mode === 'automatic'}
            >
              {slot ? 'Edit' : 'Add'}
            </Button>
            {slot && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-7 p-0"
                onClick={() => clearSlot(section, slotNumber)}
                disabled={config.mode === 'automatic'}
              >
                <Trash2 className="h-3 w-3 text-red-500" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  const renderSectionManager = (section, title, icon) => {
    const config = slotConfigs[section] || { mode: 'manual', total_slots: 6, slots: [] };
    const slots = config.slots || [];
    
    // Create array of slot numbers
    const slotNumbers = Array.from({ length: config.total_slots }, (_, i) => i + 1);
    
    return (
      <div className="space-y-4">
        {/* Section Header & Config */}
        <Card style={{ backgroundColor: '#F0EFEA', border: '1px solid #E3E1DB' }}>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {icon}
                <h3 className="font-semibold text-lg" style={{ color: '#163329' }}>{title}</h3>
              </div>
              
              <div className="flex flex-wrap items-center gap-4">
                {/* Mode Toggle */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Mode:</Label>
                  <Select
                    value={config.mode}
                    onValueChange={(value) => updateSectionConfig(section, value, config.total_slots)}
                    disabled={saving}
                  >
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Total Slots */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Slots:</Label>
                  <Select
                    value={config.total_slots.toString()}
                    onValueChange={(value) => updateSectionConfig(section, config.mode, parseInt(value))}
                    disabled={saving}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[6, 8, 10, 12, 14, 16, 18, 20].map(num => (
                        <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {config.mode === 'automatic' && (
              <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                <RefreshCw className="h-4 w-4" />
                Automatic mode: Slots are filled with newest approved products
              </p>
            )}
          </CardContent>
        </Card>
        
        {/* Slots Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {slotNumbers.map(slotNum => {
            const slot = slots.find(s => s.slot_number === slotNum);
            return renderSlotCard(section, slotNum, slot);
          })}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <RefreshCw className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#163329' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <h1 className="text-xl font-bold text-white">Ribbon Marketplace Slot Manager</h1>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('/', '_blank')}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <Eye className="h-4 w-4 mr-1" />
                Preview
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabWithTooltip 
              value="ribbon" 
              title="Ribbon" 
              description="Manage the scrolling announcement ribbon on landing page"
              className="flex items-center gap-1"
            >
              <LayoutGrid className="h-4 w-4" />
              Ribbon
            </TabWithTooltip>
            <TabWithTooltip 
              value="featured" 
              title="Featured" 
              description="Curate featured courses and products for the homepage"
              className="flex items-center gap-1"
            >
              <Sparkles className="h-4 w-4" />
              Featured
            </TabWithTooltip>
            <TabWithTooltip 
              value="new_arrivals" 
              title="New Arrivals" 
              description="Highlight recently added courses and products"
              className="flex items-center gap-1"
            >
              <Clock className="h-4 w-4" />
              New Arrivals
            </TabWithTooltip>
          </TabsList>
          
          <TabsContent value="ribbon">
            {renderSectionManager('ribbon', 'Ribbon Section', <LayoutGrid className="h-5 w-5" style={{ color: '#163329' }} />)}
          </TabsContent>
          
          <TabsContent value="featured">
            {renderSectionManager('featured', 'Featured Products', <Sparkles className="h-5 w-5" style={{ color: '#D86B47' }} />)}
          </TabsContent>
          
          <TabsContent value="new_arrivals">
            {renderSectionManager('new_arrivals', 'New Arrivals', <Clock className="h-5 w-5" style={{ color: '#163329' }} />)}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Edit Slot Dialog */}
      <Dialog open={editSlotOpen} onOpenChange={setEditSlotOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: '#163329' }}>
              Edit Slot {editingSlot?.slot_number}
            </DialogTitle>
          </DialogHeader>
          
          {editingSlot && (
            <div className="space-y-4 pt-4">
              {/* Product Selection */}
              <div>
                <Label>Link to Product (Optional)</Label>
                <Select
                  value={editingSlot.product_id || 'custom'}
                  onValueChange={handleProductSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a product or use custom content" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Content (No Product Link)</SelectItem>
                    {availableProducts.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - <Usd value={product.price} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Title */}
              <div>
                <Label>Title *</Label>
                <Input
                  value={editingSlot.custom_title}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, custom_title: e.target.value }))}
                  placeholder="Enter title"
                />
              </div>
              
              {/* Custom Subtitle */}
              <div>
                <Label>Subtitle/Description</Label>
                <Input
                  value={editingSlot.custom_subtitle}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, custom_subtitle: e.target.value }))}
                  placeholder="Enter subtitle"
                />
              </div>
              
              {/* Custom Image URL */}
              <div>
                <Label>Image</Label>
                <div className="space-y-2">
                  <Input
                    value={editingSlot.custom_image}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, custom_image: e.target.value }))}
                    placeholder="Enter image URL or upload below"
                  />
                  
                  {/* Upload Button */}
                  <div className="relative">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={isUploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading... {uploadProgress}%
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Image
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Image Preview */}
                  {editingSlot.custom_image && (
                    <div className="relative">
                      <img 
                        src={editingSlot.custom_image.startsWith('/api') ? `${API}${editingSlot.custom_image}` : editingSlot.custom_image}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded border"
                        style={{ borderColor: '#E3E1DB' }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setEditingSlot(prev => ({ ...prev, custom_image: '' }))}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Custom Price */}
              <div>
                <Label>Price (Display Only)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingSlot.custom_price}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, custom_price: e.target.value }))}
                  placeholder="99.99"
                />
              </div>
              
              {/* Custom Link */}
              <div>
                <Label>Click Link (leave empty for non-clickable)</Label>
                <Input
                  value={editingSlot.custom_link}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, custom_link: e.target.value }))}
                  placeholder="/marketplace or leave empty"
                />
              </div>
              
              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <Label>Visible on Landing Page</Label>
                <Switch
                  checked={editingSlot.is_active}
                  onCheckedChange={(checked) => setEditingSlot(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEditSlotOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => updateSlot(editingSection, editingSlot)}
                  disabled={saving || !editingSlot.custom_title}
                  style={{ backgroundColor: '#163329' }}
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save Slot
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingSlotManager;
